import React from 'react';
import { useTranslation } from 'react-i18next';
import { SplitContainer, Loader } from '@weezevent/nacre';
import Summary from './Summary';
import Stepper from './Stepper';
import AttendeesStep from './AttendeesStep';
import QuotasExceeded from './QuotasExceeded';
import { useForm } from '../../../../utils/validators';
import { Session } from '../../../../models';
import QuotasModel from '../../../../models/quotasModel';
import Inventory from '../../../../models/inventory';
import RatesTreeModel from '../../../../models/ratesTreeModel';
import css from '../styles.module.css';
import InvitationsCount from '../../../../models/invitationscount';
import { computeQuota, getUpdatedQuota, getQuotaByRate, formateRates } from '../../add-attendee/components/utils';
import { DatesFakeSeatingStep } from '../../add-attendee/components/Steps';
import SessionsStep from '../../add-attendee/components/SessionsStep';
import OrderStep from '../../add-attendee/components/OrderStep';
import { useIsEmailingBlocked } from '../../../../helpers/useIsEmailingBlocked';

export default function Steps({ organization, event }) {
    const { t } = useTranslation();
    let [step, setStep] = React.useState(1);
    let [computedQuota, setComputedQuotas] = React.useState({});
    const premiumOptions = event.feature_flags.includes('EO_INVITATION_LIGHT');
    const [datesIds, setDatesIds] = React.useState();
    const [selectedDay, setSelectedDay] = React.useState(null);

    const isEmailingBlocked = useIsEmailingBlocked();

    const [form, setForm, setFormObject] = useForm({
        currency: event?.currency,
        selected_rates: [],
        sessions: [],
        inventories: [],
        use_sessions: event?.use_sessions,
        languageLabel: event?.default_language,
        send_mail: !isEmailingBlocked,
        orders: [],
        mail_body: t('attendees.invitations.message.body', { eventTitle: event.name, lng: event.default_language }),
        mail_subject: t('attendees.invitations.message.subject', { eventTitle: event.name, lng: event.default_language })
    });

    const [sessionTree, loadingSessions] = Session.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk,
            launch: organization.pk && event.pk && event?.use_sessions,
            sync: true
        },
        [organization, event]
    );

    const [quotasModel, loadingQuotas] = QuotasModel.useApiModel(
        {
            strict: true,
            organizationId: organization.pk,
            eventId: event.pk,
            getForRates: true,
            query: {
                limit: 9999,
                ...(event.use_sessions && datesIds?.length ? { dates_ids: `[${datesIds}]` } : { id_date: 0 })
            }
        },
        [organization, event, datesIds]
    );

    const [ratesTree, loadingRates] = RatesTreeModel.useApiModel(
        {
            strict: true,
            organizationId: organization.pk,
            eventId: event.pk,
            launch: organization.pk && event.pk,
            sync: true
        },
        [organization, event]
    );

    const [invitationCount, loadingInvitationCount] = InvitationsCount.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk,
            launch: organization.pk && event.pk,
            sync: true
        },
        [organization, event]
    );

    const [inventories, loadingInventories] = Inventory.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk,
            chartId: event.chart,
            defaultLoading: false,
            launch: Boolean(event.isFakeSeating()),
            isFakeSeating: true,
            onSuccess: invt => {
                if (invt.length === 1) {
                    setForm('inventories', invt);
                }
            }
        },
        [organization, event]
    );

    const computeQuotaInvitations = React.useCallback(() => {
        return setComputedQuotas(computeQuota(ratesTree, quotasModel, event));
    }, [ratesTree, quotasModel, event]);

    const quota = React.useMemo(() => {
        return getUpdatedQuota(computedQuota, form.sessions, form.selected_rates);
    }, [form, computedQuota]);

    const searchRateIntoQuota = React.useCallback(
        ({ rateId, groupId = null, sessionId = null }) => {
            return getQuotaByRate(rateId, groupId, sessionId, quota, event);
        },
        [quota, event]
    );

    const rates = React.useMemo(() => {
        return formateRates(computedQuota, ratesTree, form?.sessions?.length ? form.sessions : sessionTree, searchRateIntoQuota);
    }, [computedQuota, ratesTree, form?.sessions, sessionTree, searchRateIntoQuota]);

    const ratesQuotasExceeded = React.useMemo(() => {
        /**
         * Return a boolean (over) to know if one of the selected rate has exceed quotas.
         */

        let over = false;

        // Retrieve the session Id. Maybe one day, we will have many sessions and this research will be inside of the foreach
        let session_id = form.sessions.length ? form.sessions[0]['weez_date_id'] : null;
        form.selected_rates?.forEach(rate => {
            // get all remaining quota on each level.
            const rate_quota = searchRateIntoQuota({ rateId: rate.rate.id, groupId: rate.rate.group, sessionId: session_id });
            const quota = Math.min(...Object.values(rate_quota).filter(value => value !== null && value !== Infinity));
            if (quota < 0) {
                // if one of them is under 0, quota are exceeded
                over = true;
            }
        });
        return over;
    }, [form, quota, searchRateIntoQuota]);

    const onChangeSteps = React.useCallback(
        stepToGo => {
            setStep(Math.max(1, stepToGo));
        },
        [setStep]
    );

    const onChangeSelectedSessions = React.useCallback(
        sessions => {
            setFormObject({ sessions: sessions, selected_rates: [] }, true);
        },
        [setFormObject]
    );

    const onChangeSelectedRates = React.useCallback(
        (selected_rates = []) => {
            setForm('selected_rates', selected_rates);
        },
        [setForm]
    );

    React.useEffect(() => {
        if (!loadingRates && !loadingQuotas && !(event?.use_sessions ? loadingSessions : false)) {
            computeQuotaInvitations();
        }
    }, [loadingRates, loadingQuotas, loadingSessions]);

    if ((event.use_sessions && loadingSessions) || loadingRates || loadingInvitationCount || loadingInventories) {
        return <Loader />;
    }

    if (ratesQuotasExceeded) {
        return <QuotasExceeded />;
    }

    return (
        <SplitContainer className={css['summary-position']} size={'maxi'} additional={<Summary form={form} type="invitations" />} onlyDesktop rootId="add-attendees">
            <Stepper step={step} onChangeSteps={onChangeSteps}>
                {event.isFakeSeatingMultipleInventories() && (
                    <DatesFakeSeatingStep
                        title={t('attendees.addAttendees.withSeats.dates-title')}
                        subtitle={t('attendees.addAttendees.withSeats.dates-subtitle')}
                        inactive={t('attendees.addAttendees.withSeats.label-dates-selected', { count: form.inventories?.length })}
                        inventories={inventories}
                        form={form}
                        setFormObject={setFormObject}
                        onChangeSteps={onChangeSteps}
                        step={step}
                        isInvitations
                    />
                )}
                {form.use_sessions && (
                    <SessionsStep
                        name="sessions_step"
                        title={t('ticket.common.seances')}
                        subtitle={t('attendees.addAttendees.withSessions.title')}
                        inactive={`${t('attendees.addAttendees.withSessions.count')}: ${form.sessions?.length}`}
                        sessions={sessionTree}
                        form={form}
                        step={step}
                        selectedDay={selectedDay}
                        setSelectedDay={setSelectedDay}
                        onChangeSelectedSessions={onChangeSelectedSessions}
                        onChangeSteps={onChangeSteps}
                        setDatesIds={setDatesIds}
                        loading={loadingSessions}
                        loadingInventories={false}
                    />
                )}
                <OrderStep
                    isInvitations
                    name="order_step"
                    title={t('attendees.invitations.order.title')}
                    subtitle={t('attendees.invitations.order.subtitle')}
                    inactive={`${t('attendees.addAttendees.withTarif.nexttitle')}: ${form.selected_rates.length}`}
                    form={form}
                    onChangeForm={setForm}
                    onChangeFormObject={setFormObject}
                    ratesTree={ratesTree}
                    rates={rates}
                    quotas={quota}
                    step={step}
                    event={event}
                    inventories={inventories}
                    onChangeSteps={onChangeSteps}
                    onChangeSelectedRates={onChangeSelectedRates}
                    loadingRates={loadingRates}
                    getQuotaForOneRate={searchRateIntoQuota}
                    computeQuota={computeQuotaInvitations}
                />
                <AttendeesStep
                    title={t('attendees.invitations.attendee.title')}
                    subtitle={t('attendees.invitations.attendee.subtitle')}
                    form={form}
                    setForm={setForm}
                    step={step}
                    event={event}
                    organization={organization}
                    onChangeSteps={onChangeSteps}
                    premiumOptions={premiumOptions}
                    invitationCount={invitationCount}
                    quota={quota}
                />
            </Stepper>
        </SplitContainer>
    );
}
